import { useEffect, useState, useMemo } from 'react';
import { useRecentItems } from './useRecentItems';
import {
  Box,
  Dialog,
  DialogContent,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import HomeIcon from '@mui/icons-material/Home';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import DescriptionIcon from '@mui/icons-material/Description';
import GroupIcon from '@mui/icons-material/Group';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import RouteIcon from '@mui/icons-material/Route';
import SettingsIcon from '@mui/icons-material/Settings';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { clearRecent, recentItemPath } from './recentItems';

interface PaletteItem {
  label: string;
  path: string;
  icon: JSX.Element;
}

export function CommandPalette({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  // Live list — kept in sync via slice-522 `itow-recents-changed` event, so
  // navigations from elsewhere update the palette without a re-open.
  const recents = useRecentItems();

  // Items are memoized on t so they update on locale change without recreation.
  const items: PaletteItem[] = useMemo(
    () => [
      { label: t('nav.home'), path: '/', icon: <HomeIcon /> },
      { label: t('nav.towRequests'), path: '/tow-requests', icon: <LocalShippingIcon /> },
      { label: t('nav.claims'), path: '/claims', icon: <DescriptionIcon /> },
      { label: t('nav.breadcrumbs.customer'), path: '/catalogs/customer', icon: <BusinessIcon /> },
      { label: t('nav.breadcrumbs.adjusters'), path: '/catalogs/adjusters', icon: <PersonIcon /> },
      { label: t('nav.breadcrumbs.drivers'), path: '/catalogs/drivers', icon: <PersonIcon /> },
      { label: t('nav.breadcrumbs.units'), path: '/catalogs/units', icon: <DirectionsCarIcon /> },
      {
        label: t('nav.breadcrumbs.towing-providers'),
        path: '/catalogs/towing-providers',
        icon: <GroupIcon />,
      },
      { label: t('nav.breadcrumbs.routings'), path: '/routings', icon: <RouteIcon /> },
      { label: t('nav.breadcrumbs.documents'), path: '/documents', icon: <DescriptionIcon /> },
      { label: t('CommandPalette.preferences'), path: '/preferences', icon: <SettingsIcon /> },
    ],
    [t],
  );

  const filtered = useMemo(() => {
    if (!query.trim()) return items;
    const q = query.toLowerCase();
    return items.filter((it) => it.label.toLowerCase().includes(q));
  }, [items, query]);

  // Reset search when palette is closed so it opens fresh next time.
  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ p: 2 }}>
          <TextField
            type="search"
            fullWidth
            autoFocus
            autoComplete="off"
            placeholder={t('CommandPalette.placeholder')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            size="small"
            inputProps={{ 'aria-label': t('CommandPalette.placeholder'), enterKeyHint: 'go' }}
            onKeyDown={(e) => {
              // Esc clears the query first, then a second Esc closes the
              // Dialog (handled by MUI's onClose). When already empty, fall
              // through so the Dialog closes on the first press.
              if (e.key === 'Escape' && query.length > 0) {
                e.stopPropagation();
                setQuery('');
                return;
              }
              // Enter activates the first matching result, but only after the
              // user has typed something — otherwise an empty palette would
              // "navigate Home" surprisingly. No-op when nothing matches.
              if (e.key === 'Enter' && query.trim().length > 0 && filtered.length > 0) {
                e.preventDefault();
                navigate(filtered[0].path);
                onClose();
              }
            }}
          />
        </Box>
        <List dense sx={{ maxHeight: 400, overflow: 'auto', pt: 0 }}>
          {/* Recent items section — only shown when the search field is empty and
              there is at least one entry in localStorage. Filtering hides recents
              so the user sees only pages that match their query. */}
          {!query.trim() && recents.length > 0 && (
            <>
              <Typography
                variant="overline"
                sx={{ px: 2, py: 0.5, display: 'block', color: 'text.secondary' }}
              >
                {t('CommandPalette.recentItems')}
              </Typography>
              {recents.map((item) => {
                const path = recentItemPath(item);
                const icon =
                  item.type === 'claim' ? <DescriptionIcon /> : <LocalShippingIcon />;
                return (
                  <ListItemButton
                    key={`recent-${item.type}-${item.id}`}
                    onClick={() => {
                      navigate(path);
                      onClose();
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>{icon}</ListItemIcon>
                    <ListItemText
                      primary={item.label}
                      secondary={
                        <Box
                          component="span"
                          sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                        >
                          <AccessTimeIcon sx={{ fontSize: '0.75rem' }} />
                          {new Date(item.visitedAt).toLocaleDateString()}
                        </Box>
                      }
                    />
                  </ListItemButton>
                );
              })}
              <ListItemButton onClick={clearRecent}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <DeleteOutlineIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={t('nav.recentClear')}
                  primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                />
              </ListItemButton>
              <Divider sx={{ my: 0.5 }} />
            </>
          )}
          {filtered.map((it) => (
            <ListItemButton
              key={it.path}
              onClick={() => {
                navigate(it.path);
                onClose();
              }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>{it.icon}</ListItemIcon>
              <ListItemText primary={it.label} />
            </ListItemButton>
          ))}
          {filtered.length === 0 && (
            <Box sx={{ p: 2, color: 'text.secondary', fontSize: '0.875rem' }}>
              {t('CommandPalette.noResults')}
            </Box>
          )}
        </List>
      </DialogContent>
    </Dialog>
  );
}
